import React from 'react';
import {Dropdown, Menu} from 'antd';
import {GlobalOutlined} from '@ant-design/icons';
import {Link, useLocation} from 'react-router-dom';
import {useLanguage} from "../contexts/LanguageContext";
import {useMediaQuery} from 'react-responsive';
import {useSidebar} from "../contexts/SidebarContext";

type LanguageType = 'en' | 'zh';

interface Translations {
    [key: string]: {
        home: string;
        cases: string;
        contact: string;
    }
}

const translations: Translations = {
    en: {
        home: 'HOME',
        cases: 'CASES',
        contact: 'CONTACT'
    },
    zh: {
        home: '首页',
        cases: '案例',
        contact: '联系我们'
    }
};

const Header: React.FC = () => {
    const location = useLocation();
    const {currentLang, setCurrentLang} = useLanguage();
    const isMobile = useMediaQuery({maxWidth: 768});
    const isVeryNarrow = useMediaQuery({maxWidth: 360});
    const {openSidebar} = useSidebar();

    const isActive = (path: string): boolean => {
        const currentPath = location.pathname;
        if (path === '/') {
            return currentPath === `/${currentLang}` || currentPath === '/';
        }
        return currentPath === `/${currentLang}${path}`;
    };

    const handleLanguageChange = (lang: LanguageType): void => {
        const currentPath = location.pathname;
        const pathSegments = currentPath.split('/').filter(Boolean);

        if (pathSegments[0] === 'en' || pathSegments[0] === 'zh') {
            pathSegments[0] = lang;
        } else {
            pathSegments.unshift(lang);
        }

        const newPath = '/' + pathSegments.join('/');
        setCurrentLang(lang);
        localStorage.setItem('language', lang);
        window.history.replaceState(null, '', newPath);
    };

    const languageMenu = (
        <Menu>
            <Menu.Item key="en" onClick={() => handleLanguageChange('en')}>
                English
            </Menu.Item>
            <Menu.Item key="zh" onClick={() => handleLanguageChange('zh')}>
                中文
            </Menu.Item>
        </Menu>
    );

    const NavLink: React.FC<{
        to?: string;
        onClick?: () => void;
        text: string;
        isActive: boolean;
    }> = ({to, onClick, text, isActive: active}) => {
        const Component = to ? Link : 'div';
        const props = to ? {to} : {onClick};

        return (
            //@ts-ignore
            <Component
                {...props}
                className={`relative group py-3 text-gray-700 hover:text-gray-900 cursor-pointer text-base`}
            >
                {text}
                <div
                    className={`absolute bottom-0 left-0 w-full h-1 bg-black transform transition-transform duration-300 
                    ${active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
                />
            </Component>
        );
    };

    return (
        <header className="w-full h-16 bg-white shadow-md fixed top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
                {/* Logo区域 */}
                <div className={`${isMobile ? 'w-24' : 'w-48'}`}>
                    <Link to={`/${currentLang}`} className="flex items-center">
                        <img
                            src="/assets/logo.png"
                            alt="logo"
                            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold"
                        />
                        {!isMobile && (
                            <span className="ml-2 text-lg font-medium">RunTop LLC</span>
                        )}
                    </Link>
                </div>

                {/* 导航区域 - 居中 */}
                <div className="flex-1 flex justify-center">
                    <nav className="flex space-x-12">
                        {!isMobile && (
                            <NavLink
                                to={`/${currentLang}`}
                                text={translations[currentLang].home}
                                isActive={isActive('/')}
                            />
                        )}
                        <NavLink
                            to={`/${currentLang}/cases`}
                            text={translations[currentLang].cases}
                            isActive={isActive('/cases')}
                        />
                        <NavLink
                            onClick={openSidebar}
                            text={translations[currentLang].contact}
                            isActive={isActive('/contact')}
                        />
                    </nav>
                </div>

                {/* 语言切换 */}
                <div className={`${isMobile ? 'w-24' : 'w-48'} flex justify-end`}>
                    <Dropdown overlay={languageMenu} placement="bottomRight">
                        <div className="flex items-center cursor-pointer text-gray-700 hover:text-gray-900">
                            <GlobalOutlined className="text-lg"/>
                            {!isVeryNarrow && (
                                <span className="ml-2">{currentLang.toUpperCase()}</span>
                            )}
                        </div>
                    </Dropdown>
                </div>
            </div>
        </header>
    );
};

export default Header;
